/**
 * Sync Service - The Brain of Offline-First Architecture
 *
 * Responsibilities:
 *   - Manage offline queue (add, process, retry)
 *   - Coordinate between local DB (IndexedDB) and remote DB (Supabase)
 *   - Handle conflicts (optimistic locking for COUNT, seamless for DELTA)
 *   - Emit sync events for UI updates
 *
 * Architecture:
 *   - Uses IndexedDB (via Dexie) for queue persistence
 *   - Uses RPC utility for safe schema targeting (leltarmester_dev)
 *   - Uses Event Bus for loose coupling
 *
 * Reference: LeltárMester Egyesített Refaktorálási Terv (v2.0)
 */

import { supabase } from '@/lib/supabaseClient';
import { db, type OfflineQueueItem } from '@/lib/db';
import { getRpcName, RPC_FUNCTIONS } from '@/utils/rpc';
import { eventBus, EVENT_TYPES } from '@/utils/eventBus';
import { logger } from './logger';
import { safeParseOfflineUser } from '../utils/safeParse';

// ============================================================================
// Types
// ============================================================================

/**
 * Sync result for a single queue item
 */
interface SyncResult {
  success: boolean;
  itemId: number;
  error?: string;
  conflict?: boolean;
  shouldRetry?: boolean;
}

/**
 * Sync progress (for UI updates)
 */
export interface SyncProgress {
  total: number;
  processed: number;
  successful: number;
  failed: number;
  current?: string; // Current operation description
}

// ============================================================================
// Sync Service
// ============================================================================

export const syncService = {
  /**
   * Add operation to offline queue
   *
   * @param operation RPC function name (e.g., 'adjust_inventory_delta', 'process_checkout_transaction')
   * @param payload RPC function payload
   * @param type Operation type (COUNT, DELTA, or CHECKOUT)
   */
  async addToQueue(
    operation: string,
    payload: unknown,
    type: 'COUNT' | 'DELTA' | 'CHECKOUT' = 'DELTA'
  ): Promise<void> {
    try {
      await db.offlineQueue.add({
        timestamp: Date.now(),
        type,
        operation,
        payload,
        retries: 0,
        lastError: null,
        status: 'pending',
      });

      logger.info(`Added to offline queue: ${operation}`, { type, payload }, 'SyncService');

      // Emit event
      eventBus.emit(EVENT_TYPES.SYNC_PROGRESS, { action: 'queued', operation });
    } catch (error) {
      logger.error('Failed to add to queue', error, 'SyncService');
      throw error;
    }
  },

  /**
   * Get current queue size
   */
  async getQueueSize(): Promise<number> {
    try {
      return await db.offlineQueue.count();
    } catch (error) {
      logger.error('Failed to get queue size', error, 'SyncService');
      return 0;
    }
  },

  /**
   * Get all pending queue items
   */
  async getPendingItems(): Promise<OfflineQueueItem[]> {
    try {
      return await db.offlineQueue.where('status').equals('pending').sortBy('timestamp');
    } catch (error) {
      logger.error('Failed to get pending items', error, 'SyncService');
      return [];
    }
  },

  /**
   * Process offline queue
   *
   * This is the BRAIN of offline-first sync.
   * Processes items one by one, respects COUNT vs DELTA strategies.
   */
  async processQueue(): Promise<SyncProgress> {
    logger.info('Starting queue processing', undefined, 'SyncService');

    // Emit sync started event
    eventBus.emit(EVENT_TYPES.SYNC_STARTED);

    const progress: SyncProgress = {
      total: 0,
      processed: 0,
      successful: 0,
      failed: 0,
    };

    try {
      // 1. Validate session (with offline fallback)
      let user = null;
      try {
        const { data, error: authError } = await supabase.auth.getUser();
        if (data?.user) {
          user = data.user;
          logger.debug('[SyncService] Auth check successful', { userId: user.id }, 'SyncService');
        }
      } catch (error) {
        logger.warn('[SyncService] Auth check failed, trying cached user', error, 'SyncService');
      }

      // Fallback to cached user if offline
      if (!user) {
        const cachedUserStr = localStorage.getItem('offline_user');
        const cachedUser = safeParseOfflineUser(cachedUserStr);
        if (cachedUser) {
          user = { id: cachedUser.id } as any;
          logger.info(
            '[SyncService] Using cached user for sync',
            { userId: cachedUser.id },
            'SyncService'
          );
        }
      }

      // Only fail if BOTH checks failed
      if (!user) {
        logger.error(
          'Cannot sync without valid session (no cached user)',
          undefined,
          'SyncService'
        );
        eventBus.emit(EVENT_TYPES.SYNC_FAILED, { reason: 'No session' });
        return progress;
      }

      // 2. Get pending items
      const pendingItems = await this.getPendingItems();
      progress.total = pendingItems.length;

      if (pendingItems.length === 0) {
        logger.info('Queue is empty', undefined, 'SyncService');
        eventBus.emit(EVENT_TYPES.SYNC_COMPLETED, progress);
        return progress;
      }

      logger.info(`Processing ${pendingItems.length} queue items`, undefined, 'SyncService');

      // 3. Process each item
      for (const item of pendingItems) {
        progress.current = `${item.operation} (${item.type})`;

        // Emit progress
        eventBus.emit(EVENT_TYPES.SYNC_PROGRESS, progress);

        // Process item based on type
        const result = await this.processSingleItem(item);

        progress.processed++;

        if (result.success) {
          progress.successful++;
          // Remove from queue
          await db.offlineQueue.delete(item.id!);
          logger.info(`Queue item ${item.id} processed successfully`, undefined, 'SyncService');
        } else {
          progress.failed++;

          // Handle failure
          if (result.shouldRetry && item.retries < 3) {
            // Retry later
            await db.offlineQueue.update(item.id!, {
              retries: item.retries + 1,
              lastError: result.error || 'Unknown error',
              status: 'pending',
            });
            logger.warn(`Queue item ${item.id} failed, will retry`, result, 'SyncService');
          } else {
            // Mark as failed permanently
            await db.offlineQueue.update(item.id!, {
              status: 'failed',
              lastError: result.error || 'Max retries exceeded',
            });
            logger.error(`Queue item ${item.id} failed permanently`, result, 'SyncService');
          }
        }
      }

      // 4. Emit sync completed
      logger.info('Queue processing complete', progress, 'SyncService');
      eventBus.emit(EVENT_TYPES.SYNC_COMPLETED, progress);

      return progress;
    } catch (error) {
      logger.error('Queue processing failed', error, 'SyncService');
      eventBus.emit(EVENT_TYPES.SYNC_FAILED, { error });
      return progress;
    }
  },

  /**
   * Process a single queue item
   *
   * Handles COUNT vs DELTA strategies.
   */
  async processSingleItem(item: OfflineQueueItem): Promise<SyncResult> {
    try {
      // Update status to processing
      await db.offlineQueue.update(item.id!, { status: 'processing' });

      // Determine RPC function name (with schema suffix)
      let rpcName: string;

      if (item.operation === 'set_inventory_count_with_version') {
        rpcName = getRpcName(RPC_FUNCTIONS.SET_INVENTORY_COUNT_WITH_VERSION);
      } else if (item.operation === 'adjust_inventory_delta') {
        rpcName = getRpcName(RPC_FUNCTIONS.ADJUST_INVENTORY_DELTA);
      } else if (item.operation === 'adjust_inventory_batch') {
        rpcName = getRpcName(RPC_FUNCTIONS.ADJUST_INVENTORY_BATCH);
      } else if (item.operation === 'process_checkout_transaction') {
        // PART 2 HOTFIX: Handle POS checkout transactions
        rpcName = getRpcName(RPC_FUNCTIONS.PROCESS_CHECKOUT_TRANSACTION);
        logger.info('Processing queued checkout transaction', { itemId: item.id }, 'SyncService');
      } else if (item.operation === 'process_inventory_transaction') {
        // INVENTORY MODE: Handle inventory counting transactions (uses _v2)
        rpcName = getRpcName(RPC_FUNCTIONS.PROCESS_INVENTORY_TRANSACTION);
        logger.info('Processing queued inventory transaction', { itemId: item.id }, 'SyncService');
      } else {
        // Fallback: use operation as-is with getRpcName
        rpcName = getRpcName(item.operation);
      }

      logger.debug(`Calling RPC: ${rpcName}`, item.payload, 'SyncService');

      // Call RPC
      const { data, error } = await supabase.rpc(rpcName, item.payload as Record<string, unknown>);

      if (error) {
        logger.error(`RPC call failed: ${rpcName}`, error, 'SyncService');

        // Check if this is a fatal error (business logic, RLS, etc.)
        const isFatalError = this.isFatalError(error);

        return {
          success: false,
          itemId: item.id!,
          error: error.message,
          shouldRetry: !isFatalError,
        };
      }

      // Check result for conflict (COUNT mode only)
      if (item.type === 'COUNT' && data) {
        // Strategy A: Optimistic Locking result
        const result = Array.isArray(data) ? data[0] : data;

        if (result.conflict === true) {
          logger.warn(`Conflict detected for item ${item.id}`, result, 'SyncService');

          // Emit conflict event (UI can handle this)
          eventBus.emit(EVENT_TYPES.SYNC_FAILED, {
            itemId: item.id,
            conflict: true,
            payload: item.payload,
            currentVersion: result.current_version,
          });

          return {
            success: false,
            itemId: item.id!,
            error: result.message || 'Version conflict',
            conflict: true,
            shouldRetry: false, // Don't retry conflicts automatically
          };
        }
      }

      // Success
      logger.info(`RPC call succeeded: ${rpcName}`, data, 'SyncService');

      return {
        success: true,
        itemId: item.id!,
      };
    } catch (error: unknown) {
      logger.error(`Failed to process queue item ${item.id}`, error, 'SyncService');

      return {
        success: false,
        itemId: item.id!,
        error: error instanceof Error ? error.message : String(error),
        shouldRetry: true,
      };
    }
  },

  /**
   * Check if error is fatal (should not retry)
   */
  isFatalError(error: { message?: string; code?: string }): boolean {
    const message = error.message?.toLowerCase() || '';
    const code = error.code || '';

    // Fatal errors (business logic, RLS, invalid data):
    return (
      message.includes('insufficient stock') ||
      message.includes('violates row-level security') ||
      message.includes('row-level security') ||
      message.includes('item not found') ||
      message.includes('invalid') ||
      code === 'P0001' || // PostgreSQL RAISE EXCEPTION
      code === 'PGRST301' || // RLS policy violation
      (code.startsWith('P') && code.length === 5) // PostgreSQL error codes
    );
  },

  /**
   * Clear all failed items from queue
   */
  async clearFailedItems(): Promise<number> {
    try {
      const deleted = await db.offlineQueue.where('status').equals('failed').delete();
      logger.info(`Cleared ${deleted} failed items from queue`, undefined, 'SyncService');
      return deleted;
    } catch (error) {
      logger.error('Failed to clear failed items', error, 'SyncService');
      return 0;
    }
  },

  /**
   * Clear entire queue (for debugging or reset)
   */
  async clearQueue(): Promise<void> {
    try {
      await db.offlineQueue.clear();
      logger.warn('Queue cleared completely', undefined, 'SyncService');
      eventBus.emit(EVENT_TYPES.CACHE_CLEARED, { type: 'queue' });
    } catch (error) {
      logger.error('Failed to clear queue', error, 'SyncService');
      throw error;
    }
  },

  /**
   * Get queue statistics (for debugging)
   */
  async getQueueStats() {
    try {
      const pending = await db.offlineQueue.where('status').equals('pending').count();
      const processing = await db.offlineQueue.where('status').equals('processing').count();
      const failed = await db.offlineQueue.where('status').equals('failed').count();
      const total = await db.offlineQueue.count();

      return {
        total,
        pending,
        processing,
        failed,
      };
    } catch (error) {
      logger.error('Failed to get queue stats', error, 'SyncService');
      return {
        total: 0,
        pending: 0,
        processing: 0,
        failed: 0,
      };
    }
  },
};

// ============================================================================
// Auto-sync on network change (optional)
// ============================================================================

// Monitor online/offline status
window.addEventListener('online', () => {
  logger.info('Network online, starting auto-sync', undefined, 'SyncService');
  eventBus.emit(EVENT_TYPES.ONLINE);

  // Auto-process queue when coming back online
  syncService.processQueue().catch((error) => {
    logger.error('Auto-sync failed', error, 'SyncService');
  });
});

window.addEventListener('offline', () => {
  logger.info('Network offline', undefined, 'SyncService');
  eventBus.emit(EVENT_TYPES.OFFLINE);
});
